/**
 * Swipe-down to put the Sounds sheet away at phone width.
 *
 * A drag that starts on one of the handles (the sheet's header) follows the
 * finger; one that starts on the scrolling list does so only while the list
 * is at its top and the finger moves down, so a pull past the start drags the
 * sheet instead of fighting the scroll. On release the sheet is dismissed when
 * the drag went far or fast enough, and snaps back otherwise. Horizontal
 * drags are left alone from the first clear horizontal move.
 */

export interface SheetSwipeDecision {
    readonly dy: number;
    readonly dt_ms: number;
    readonly sheetHeight: number;
}

/** Far enough (a quarter of the sheet, at least 80px) or fast enough (a flick). */
export const shouldDismissSheet = ({ dy, dt_ms, sheetHeight }: SheetSwipeDecision): boolean => {
    if (dy <= 0) {
        return false;
    }
    const velocity = dt_ms > 0 ? dy / dt_ms : 0;
    return dy > Math.max(80, sheetHeight * 0.25) || (velocity > 0.6 && dy > 24);
};

export interface SheetSwipeOptions {
    /** Elements a drag may start on anywhere. */
    readonly handles: readonly HTMLElement[];
    /** The scrolling region: a drag may start here only at scroll top, moving down. */
    readonly scrollEl: HTMLElement;
    /** Checked at the start of every touch; false leaves the sheet alone. */
    readonly isEnabled: () => boolean;
    readonly onDismiss: () => void;
}

const kSettleTransition = "transform 200ms cubic-bezier(0.32, 0.72, 0, 1)";

export const attachSheetSwipe = (sheet: HTMLElement, options: SheetSwipeOptions): void => {
    let startY = 0;
    let startX = 0;
    let startedAt = 0;
    let isTracking = false;
    let isDragging = false;
    let didAbandon = false;

    const settle = (): void => {
        sheet.style.transition = kSettleTransition;
        sheet.style.transform = "";
        const clear = (): void => {
            sheet.style.transition = "";
            sheet.removeEventListener("transitionend", clear);
        };
        sheet.addEventListener("transitionend", clear);
    };

    sheet.addEventListener(
        "touchstart",
        (event) => {
            if (!options.isEnabled() || event.touches.length !== 1) {
                return;
            }
            const target = event.target instanceof Node ? event.target : null;
            const onHandle = options.handles.some((h) => target !== null && h.contains(target));
            const onList =
                target !== null &&
                options.scrollEl.contains(target) &&
                options.scrollEl.scrollTop <= 0;
            if (!onHandle && !onList) {
                return;
            }
            const touch = event.touches[0];
            startY = touch.clientY;
            startX = touch.clientX;
            startedAt = event.timeStamp;
            isTracking = true;
            isDragging = false;
            didAbandon = false;
        },
        { passive: true },
    );

    sheet.addEventListener(
        "touchmove",
        (event) => {
            if (!isTracking || didAbandon) {
                return;
            }
            const touch = event.touches[0];
            const dy = touch.clientY - startY;
            const dx = touch.clientX - startX;
            if (!isDragging) {
                // Decide the gesture's direction once, on the first clear move.
                if (Math.abs(dx) < 6 && Math.abs(dy) < 6) {
                    return;
                }
                if (Math.abs(dx) > Math.abs(dy) || dy < 0) {
                    didAbandon = true;
                    return;
                }
                isDragging = true;
                sheet.style.transition = "none";
            }
            event.preventDefault();
            sheet.style.transform = `translateY(${Math.max(0, dy)}px)`;
        },
        { passive: false },
    );

    const end = (event: TouchEvent): void => {
        if (!isTracking) {
            return;
        }
        isTracking = false;
        if (!isDragging) {
            return;
        }
        isDragging = false;
        const touch = event.changedTouches[0];
        const dy = touch.clientY - startY;
        const shouldDismiss =
            event.type === "touchend" &&
            shouldDismissSheet({
                dy,
                dt_ms: event.timeStamp - startedAt,
                sheetHeight: sheet.getBoundingClientRect().height,
            });
        if (shouldDismiss) {
            // Let the sheet's own closed transform take it the rest of the way.
            sheet.style.transition = "";
            sheet.style.transform = "";
            options.onDismiss();
            return;
        }
        settle();
    };
    sheet.addEventListener("touchend", end);
    sheet.addEventListener("touchcancel", end);
};
